import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI, ensureCsrfToken } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      await ensureCsrfToken();
      const result = await authAPI.me();
      setUser(result.authenticated ? result.user : null);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (...args) => {
    const result = await authAPI.login(...args);
    setUser(result.user);
    return result;
  }, []);

  const register = useCallback(async (data) => {
    const result = await authAPI.register(data);
    if (result.user) setUser(result.user);
    return result;
  }, []);

  const updateNickname = useCallback(async (username) => {
    const result = await authAPI.updateAccount(username);
    setUser(result.user);
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authAPI.logout();
    setUser(null);
    await ensureCsrfToken(true);
  }, []);

  const value = useMemo(() => ({ user, loading, isAuthenticated: Boolean(user), login, register, logout, refresh, updateNickname }), [user, loading, login, register, logout, refresh, updateNickname]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
